import { useEffect } from 'react';
import styled from 'styled-components';
import {
  Button,
  FlexColumn,
  FormControls,
  Loading,
  LoadingButton,
  SegmentedButtonGroup,
  SegmentedButton,
  TextField,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import { ValueLabelOption } from '@elementary/common-ui/types';
import { Autocomplete } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { alertIntegrationLabel } from 'shared/constants';
import {
  IntegrationType,
  AlertDestinationFormData,
  ConfigurationType,
} from 'shared/types';
import { isValidEmail } from 'shared/utils/validators';
import AlertDestinationIcon from './AlertDestinationIcon';
import EmailsAutocompleteField from './EmailsAutocompleteField';

const FormWrapper = styled(FlexColumn)`
  border: 1px solid ${({ theme }) => theme.palette.common.gray5};
  border-radius: 8px;
  gap: 16px;
  padding: 16px;
`;

type Props = {
  alertDestination?: {
    type: IntegrationType;
    channel: ValueLabelOption | ValueLabelOption[];
    configurationType?: ConfigurationType;
    ruleId?: string;
  };
  destinationTypes?: IntegrationType[];
  destinationOptions: Record<
    IntegrationType,
    {
      id: string;
      options: ValueLabelOption[];
      isLoaded: boolean;
    }
  >;
  autoSubmit?: boolean;
  onSubmit: (data: AlertDestinationFormData) => void;
  onClose: () => void;
  isLoading?: boolean;
  error?: boolean;
  providersError?: boolean;
  isLoadingDestinations?: boolean;
};

const AlertDestinationForm = ({
  alertDestination,
  destinationTypes = [],
  destinationOptions,
  autoSubmit,
  onSubmit,
  onClose,
  isLoading,
  error,
  providersError,
  isLoadingDestinations,
}: Props) => {
  const defaultValues: AlertDestinationFormData = alertDestination
    ? alertDestination?.type === 'email'
      ? {
          alertProvider: 'email' as const,
          destination: Array.isArray(alertDestination?.channel)
            ? alertDestination?.channel
            : [alertDestination?.channel],
        }
      : {
          alertProvider: alertDestination?.type,
          destination: Array.isArray(alertDestination?.channel)
            ? alertDestination?.channel[0]
            : alertDestination?.channel,
        }
    : {
        alertProvider: destinationTypes[0],
        destination: undefined,
      };

  const methods = useForm<AlertDestinationFormData>({
    defaultValues,
  });

  const alertProvider = methods.watch('alertProvider');
  const destination = methods.watch('destination');
  const allowFreeSolo = !destinationOptions[alertProvider]?.isLoaded;

  const hasInvalidEmails = (): boolean => {
    if (alertProvider !== 'email' || !destination) return false;

    const emailList = Array.isArray(destination) ? destination : [destination];
    return emailList.some((item) => {
      const emailValue = typeof item === 'string' ? item : item?.value || '';
      return !isValidEmail(emailValue);
    });
  };

  useEffect(() => {
    if (!alertDestination?.channel) {
      methods.setValue('destination', undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertProvider]);

  const handleSubmit = () => {
    const formData = methods.watch();
    const destination = formData.destination;

    if (!destination) return;

    if (formData.alertProvider === 'email') {
      const destinations = Array.isArray(destination)
        ? destination
        : [destination];
      const processedDestinations = destinations.map((dest) =>
        typeof dest === 'string' ? { label: dest, value: dest } : dest,
      );

      onSubmit({
        alertProvider: 'email',
        destination: processedDestinations,
      });
    } else {
      const processedDestination = Array.isArray(destination)
        ? destination[0]
        : destination;

      onSubmit({
        alertProvider: formData.alertProvider,
        destination: processedDestination,
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FormWrapper>
          {isLoading || isLoadingDestinations ? (
            <Loading size={32} />
          ) : (
            <>
              <FormControls.Row>
                <FormControls.FormText sx={{ whiteSpace: 'nowrap' }}>
                  Alert provider
                </FormControls.FormText>

                {providersError && (
                  <FormControls.ErrorMessage
                    sx={{
                      position: 'unset !important',
                      whiteSpace: 'pre-wrap !important',
                      lineHeight: '14px !important',
                    }}
                  >
                    Error loading alert providers. Please verify your{' '}
                    <b>messaging and incident management integrations’ </b>
                    connections and try again.
                  </FormControls.ErrorMessage>
                )}

                <Controller
                  name="alertProvider"
                  control={methods.control}
                  rules={{ required: requiredLabel }}
                  render={({ field }) => (
                    <SegmentedButtonGroup
                      value={field.value}
                      onChange={(_, value) => {
                        if (value != null) {
                          field.onChange(value);
                          methods.setValue('destination', undefined);
                        }
                      }}
                      exclusive
                      size="small"
                    >
                      {destinationTypes.map((destType) => (
                        <SegmentedButton
                          key={destType}
                          title={alertIntegrationLabel[destType]}
                          value={destType}
                          size="small"
                        >
                          <AlertDestinationIcon type={destType} size={18} />
                        </SegmentedButton>
                      ))}
                    </SegmentedButtonGroup>
                  )}
                />
              </FormControls.Row>

              <FormControls.Row>
                <FormControls.FormText
                  sx={{
                    alignSelf: 'start',
                    pt: '10px', // to align with the input field in origin height
                  }}
                >
                  Destination name
                </FormControls.FormText>

                {methods.watch('alertProvider') === 'email' ? (
                  <EmailsAutocompleteField
                    methods={methods}
                    emails={destinationOptions['email']?.options || []}
                    autoSubmit={autoSubmit}
                    onAutoSubmit={handleSubmit}
                  />
                ) : (
                  <FormControls.InputWrapper style={{ flex: 1 }}>
                    <Controller
                      name="destination"
                      control={methods.control}
                      rules={{ required: requiredLabel }}
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          loadingText="Loading destinations..."
                          options={destinationOptions[alertProvider]?.options}
                          freeSolo={allowFreeSolo}
                          disabled={!methods.watch('alertProvider')}
                          onChange={(_, val) => {
                            // When selecting from dropdown, val is an object
                            // When clearing, val is null
                            // In freeSolo mode when pressing Enter, val can be a string
                            if (typeof val === 'string') {
                              field.onChange({ label: val, value: val });
                            } else {
                              field.onChange(val);
                            }
                            if (autoSubmit) {
                              handleSubmit();
                            }
                          }}
                          onInputChange={(_, inputValue, reason) => {
                            // In freeSolo mode, update form value as user types
                            // Only update on 'input' reason (user typing), not 'reset' or 'clear'
                            if (allowFreeSolo && reason === 'input') {
                              field.onChange({
                                label: inputValue,
                                value: inputValue,
                              });
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.stopPropagation();
                              if (field.value) {
                                handleSubmit();
                              }
                            }
                          }}
                          onBlur={() => {
                            if (autoSubmit) handleSubmit();
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="large"
                              placeholder={
                                allowFreeSolo
                                  ? 'Enter destination'
                                  : 'Choose destination'
                              }
                            />
                          )}
                        />
                      )}
                    />
                  </FormControls.InputWrapper>
                )}
              </FormControls.Row>

              <FormControls.InputWrapper>
                {error ? (
                  <FormControls.ErrorMessage>
                    Something went wrong. Please try again.
                  </FormControls.ErrorMessage>
                ) : null}
              </FormControls.InputWrapper>

              {!autoSubmit && (
                <FormControls.Row style={{ justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      onClose();
                      methods.reset();
                    }}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    disabled={
                      !methods.formState.isValid ||
                      hasInvalidEmails() ||
                      isLoadingDestinations
                    }
                    onClick={handleSubmit}
                    loading={isLoading}
                  >
                    {alertDestination ? 'Save' : 'Add'}
                  </LoadingButton>
                </FormControls.Row>
              )}
            </>
          )}
        </FormWrapper>
      </form>
    </FormProvider>
  );
};

export default AlertDestinationForm;
